import { asLocalDate } from "@freightsimple/time/branded-types";
import type { LocalDate } from "@freightsimple/time/branded-types";
import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { FormGroup } from "@freightsimple/ui-core/FormLayout/FormGroup";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Tab } from "@freightsimple/ui-core/PageLayout/Tab";
import { Tabs } from "@freightsimple/ui-core/PageLayout/Tabs";
import { DateRangePicker } from "@freightsimple/ui-core/DateTime/Controls/DateRangePicker";
import ShipmentFilterCarriersDropdown from "@/Shared/ShipmentFilter/Dropdowns/ShipmentFilterCarriersDropdown";
import ShipmentFilterShipmentStatesDropdown from "@/Shared/ShipmentFilter/Dropdowns/ShipmentFilterShipmentStatesDropdown";
import ShipmentFilterStateProvinceDropdown from "@/Shared/ShipmentFilter/Dropdowns/ShipmentFilterStateProvinceDropdown";
import {
  FilterRow,
  FreightClaimsRow,
  PriceRow,
} from "@/Shared/ShipmentFilter/Dialogs/Internal/filterShipmentsDialogRows";

/**
 * The filter description stores plain "YYYY-MM-DD" strings. LocalDate is a brand
 * over exactly that string, so this applies the brand rather than converting.
 */
function asFilterDate(value: string | undefined): LocalDate | undefined {
  return value === undefined ? undefined : asLocalDate(value);
}

export interface FilterShipmentsDescription {
  proNumber?: string | undefined;
  bolNumber?: string | undefined;
  carrierIdentifier?: string | undefined;
  minPriceDollars?: number | undefined;
  maxPriceDollars?: number | undefined;
  shipmentState?: ShipmentState | undefined;
  pickupReferenceNumber?: string | undefined;
  pickupBusinessName?: string | undefined;
  pickupPostalCode?: string | undefined;
  pickupStateOrProvinceCode?: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  deliveryBusinessName?: string | undefined;
  deliveryPostalCode?: string | undefined;
  deliveryStateOrProvinceCode?: string | undefined;
  bookedAtStartRange?: string | undefined;
  bookedAtEndRange?: string | undefined;
  pickupDateStartRange?: string | undefined;
  pickupDateEndRange?: string | undefined;
  deliveredAtStartRange?: string | undefined;
  deliveredAtEndRange?: string | undefined;
  onlyShowFreightClaims?: boolean | undefined;
}

function countSet(
  values: (string | number | boolean | undefined)[],
): number | undefined {
  const set = values.filter(
    (v) => v !== undefined && v !== false && v !== "",
  ).length;
  return set === 0 ? undefined : set;
}

function rangeIsSet(start: string | undefined, end: string | undefined) {
  return start !== undefined || end !== undefined;
}

/**
 * How many filters each tab is holding, shown as a count on the tab itself.
 * Tabs hide whatever isn't on screen, so without this you'd have to click
 * through all four to work out why the list came back empty.
 *
 * A date range counts once, not twice — "booked between two dates" is one
 * filter to the reader even though it is two fields.
 */
function tabCounts(filter: FilterShipmentsDescription) {
  return {
    general: countSet([
      filter.proNumber,
      filter.bolNumber,
      filter.carrierIdentifier,
      filter.minPriceDollars,
      filter.maxPriceDollars,
      filter.shipmentState,
      filter.onlyShowFreightClaims,
    ]),
    dates: countSet([
      rangeIsSet(filter.bookedAtStartRange, filter.bookedAtEndRange),
      rangeIsSet(filter.pickupDateStartRange, filter.pickupDateEndRange),
      rangeIsSet(filter.deliveredAtStartRange, filter.deliveredAtEndRange),
    ]),
    pickup: countSet([
      filter.pickupReferenceNumber,
      filter.pickupBusinessName,
      filter.pickupPostalCode,
      filter.pickupStateOrProvinceCode,
    ]),
    delivery: countSet([
      filter.deliveryReferenceNumber,
      filter.deliveryBusinessName,
      filter.deliveryPostalCode,
      filter.deliveryStateOrProvinceCode,
    ]),
  };
}

interface FilterShipmentsDialogBodyProps {
  onConfirm: (filterShipmentDescription: FilterShipmentsDescription) => void;
  onCancel: () => void;
  filterDescription: FilterShipmentsDescription;
}

export function FilterShipmentsDialogBody(
  props: FilterShipmentsDialogBodyProps,
) {
  const [filterShipmentsDescripton, setFilterShipmentsDescription] = useState(
    props.filterDescription,
  );

  const counts = tabCounts(filterShipmentsDescripton);

  // No ControlPresentationProvider here: the dialog takes the default boxed
  // look. Underlined is the customer booking flow's language, where a field is
  // the point of the screen; twenty mostly-empty filters need an edge to read
  // against, and the four columns of bare underlines read as a wireframe.
  return (
    <Stack gap={6}>
      <Tabs>
        <Tab id="general" title="General" count={counts.general}>
          <FormGroup labelPosition="beside" labelWidth="lg">
            <FilterRow
              title="PRO Number"
              value={filterShipmentsDescripton.proNumber || ""}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  proNumber: newValue,
                }));
              }}
            />
            <FilterRow
              title="BOL Number"
              value={filterShipmentsDescripton.bolNumber || ""}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  bolNumber: newValue,
                }));
              }}
            />
            <ShipmentFilterCarriersDropdown
              carrierIdentifier={filterShipmentsDescripton.carrierIdentifier}
              onCarrierIdentifierChanged={function (newCarrierIdentfier) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  carrierIdentifier: newCarrierIdentfier,
                }));
              }}
            />
            <ShipmentFilterShipmentStatesDropdown
              shipmentState={filterShipmentsDescripton.shipmentState}
              onShipmentStateChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  shipmentState: newValue,
                }));
              }}
            />
            <PriceRow
              minPrice={filterShipmentsDescripton.minPriceDollars}
              maxPrice={filterShipmentsDescripton.maxPriceDollars}
              onMinPriceChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  minPriceDollars: newValue,
                }));
              }}
              onMaxPriceChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  maxPriceDollars: newValue,
                }));
              }}
            />
            <FreightClaimsRow
              onHasClaimChange={(value) => {
                setFilterShipmentsDescription((prev) => ({
                  ...prev,
                  onlyShowFreightClaims: value === true ? true : undefined,
                }));
              }}
              onlyShowClaims={
                filterShipmentsDescripton.onlyShowFreightClaims ?? false
              }
            />
          </FormGroup>
        </Tab>

        <Tab id="dates" title="Dates" count={counts.dates}>
          <FormGroup labelPosition="beside" labelWidth="lg">
            {/* Labelled rather than left to the pickers' own "Booked From /
                Booked To" placeholders, which vanish the moment a date is
                picked and leave three identical-looking ranges. */}
            <FormItem label="Booked" fillWidth>
              <DateRangePicker
                allowOpenEnded
                allowClear
                placeholder={["From", "To"]}
                value={[
                  asFilterDate(filterShipmentsDescripton.bookedAtStartRange),
                  asFilterDate(filterShipmentsDescripton.bookedAtEndRange),
                ]}
                onChange={function (range) {
                  setFilterShipmentsDescription(
                    (filterShipmentsDescripton) => ({
                      ...filterShipmentsDescripton,
                      bookedAtStartRange: range?.[0],
                      bookedAtEndRange: range?.[1],
                    }),
                  );
                }}
              />
            </FormItem>
            <FormItem label="Picked Up" fillWidth>
              <DateRangePicker
                allowOpenEnded
                allowClear
                placeholder={["From", "To"]}
                value={[
                  asFilterDate(filterShipmentsDescripton.pickupDateStartRange),
                  asFilterDate(filterShipmentsDescripton.pickupDateEndRange),
                ]}
                onChange={function (range) {
                  setFilterShipmentsDescription(
                    (filterShipmentsDescripton) => ({
                      ...filterShipmentsDescripton,
                      pickupDateStartRange: range?.[0],
                      pickupDateEndRange: range?.[1],
                    }),
                  );
                }}
              />
            </FormItem>
            <FormItem label="Delivered" fillWidth>
              <DateRangePicker
                allowOpenEnded
                allowClear
                placeholder={["From", "To"]}
                value={[
                  asFilterDate(filterShipmentsDescripton.deliveredAtStartRange),
                  asFilterDate(filterShipmentsDescripton.deliveredAtEndRange),
                ]}
                onChange={function (range) {
                  setFilterShipmentsDescription(
                    (filterShipmentsDescripton) => ({
                      ...filterShipmentsDescripton,
                      deliveredAtStartRange: range?.[0],
                      deliveredAtEndRange: range?.[1],
                    }),
                  );
                }}
              />
            </FormItem>
          </FormGroup>
        </Tab>

        <Tab id="pickup" title="Pickup" count={counts.pickup}>
          <FormGroup labelPosition="beside" labelWidth="lg">
            <FilterRow
              title="Reference Number"
              value={filterShipmentsDescripton.pickupReferenceNumber}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  pickupReferenceNumber: newValue,
                }));
              }}
            />
            <FilterRow
              title="Business Name"
              value={filterShipmentsDescripton.pickupBusinessName}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  pickupBusinessName: newValue,
                }));
              }}
            />
            <FilterRow
              title="Postal Code"
              value={filterShipmentsDescripton.pickupPostalCode}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  pickupPostalCode: newValue,
                }));
              }}
            />
            <ShipmentFilterStateProvinceDropdown
              label="Pickup State / Province"
              stateOrProvinceCode={
                filterShipmentsDescripton.pickupStateOrProvinceCode
              }
              onStateOrProvinceCodeChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  pickupStateOrProvinceCode: newValue,
                }));
              }}
            />
          </FormGroup>
        </Tab>

        <Tab id="delivery" title="Delivery" count={counts.delivery}>
          <FormGroup labelPosition="beside" labelWidth="lg">
            <FilterRow
              title="Reference Number"
              value={filterShipmentsDescripton.deliveryReferenceNumber}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  deliveryReferenceNumber: newValue,
                }));
              }}
            />
            <FilterRow
              title="Business Name"
              value={filterShipmentsDescripton.deliveryBusinessName}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  deliveryBusinessName: newValue,
                }));
              }}
            />
            <FilterRow
              title="Postal Code"
              value={filterShipmentsDescripton.deliveryPostalCode}
              placeholder=""
              onChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  deliveryPostalCode: newValue,
                }));
              }}
            />
            <ShipmentFilterStateProvinceDropdown
              label="Delivery State / Province"
              stateOrProvinceCode={
                filterShipmentsDescripton.deliveryStateOrProvinceCode
              }
              onStateOrProvinceCodeChange={function (newValue) {
                setFilterShipmentsDescription((filterShipmentsDescripton) => ({
                  ...filterShipmentsDescripton,
                  deliveryStateOrProvinceCode: newValue,
                }));
              }}
            />
          </FormGroup>
        </Tab>
      </Tabs>

      <Row gap={4} justify="end">
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button
          primary
          onClick={function () {
            props.onConfirm(filterShipmentsDescripton);
          }}
        >
          Confirm
        </Button>
      </Row>
    </Stack>
  );
}
